import CheckedLabel from "@/components/core/labels/CheckedLabel";
import {
  requestDeleteUserPicture,
  requestGetUserSignaturePictures,
  requestUpdateUserPicture,
  requestUpdateUserSignaturePriority,
  requestUserGetSignaturePriority,
} from "@/state/users/UserEffects";
import { Picture } from "@/type/core/offices/offices.types";
import {
  HANDWRITTEN_SIGNATURE_RANKS,
  PERSONAL_SIGNATURE_IMAGE_RANKS,
  SIGNATURE_TEXT_RANKS,
} from "@/utils/Constant";
import { SignaturePicturePriorityEnum } from "@/utils/enums/signature.picture.priority.enum";
import { toastError, toastSuccess } from "@/utils/helpers/toast-helper";
import InfoModal from "@components/core/modals/InfoModal";
import { FunctionComponent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SignatureHandwrittenPicture from "@components/core/subComponents/signature/SignatureHandwrittenPicture";
import SignatureNoPicture from "@components/core/subComponents/signature/SignatureNoPicture";
import SignaturePicture from "@components/core/subComponents/signature/SignaturePicture";
import SignatureTextPicture from "@components/core/subComponents/signature/SignatureTextPicture";
import CardSection from "@/components/core/section/CardSection";
import BasicLink from "@/components/core/links/BasicLink";
import BasicButton from "@/components/core/buttons/BasicButton";
import { ROUTES } from "@/routes/Routes";

const DEFAULT_PICTURES: Picture[] = [
  {
    rank: 1,
    picture: "",
  },
  {
    rank: 2,
    picture: "",
  },
  {
    rank: 3,
    picture: "",
  },
  {
    rank: 4,
    picture: "",
  },
  {
    rank: 5,
    picture: "",
  },
];

const MySignaturesSection: FunctionComponent = () => {
  const { t } = useTranslation();

  const [signaturePictures, setSignaturePictures] = useState<Picture[]>([]);
  const [signaturePriority, setSignaturePriority] =
    useState<SignaturePicturePriorityEnum>();

  useEffect(() => {
    void requestGetUserSignaturePictures({});
  }, []);

  useEffect(() => {
    return requestGetUserSignaturePictures.done.watch(({ result }) => {
      if (result.ok && result.data) {
        ensureAllRanksPresent(result.data.sort((a, b) => a.rank - b.rank));
      } else {
        toastError(t("error.user.signaturePictures"));
      }
    });
  });

  useEffect(() => {
    void requestUserGetSignaturePriority({});
  }, []);

  useEffect(() => {
    return requestUserGetSignaturePriority.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setSignaturePriority(result.data);
      } else {
        toastError(t("error.user.getSignaturePriority"));
      }
    });
  }, [t]);

  const handleUpdatePicture = (
    rank: number,
    file?: File,
    callback?: () => void,
  ) => {
    const formData = new FormData();
    formData.append("signaturePicture", file || "none");
    formData.append(
      "rank",
      new Blob([`${rank}`], { type: "application/json" }),
      "",
    );
    void requestUpdateUserPicture({ dto: formData })
      .then(() => {
        toastSuccess(t("success.userPicture.update"));
        callback?.();
        void requestGetUserSignaturePictures({});
      })
      .catch(() => {
        toastError(t("error.toast.userPicture.update"));
      });
  };

  const handleDeletePicture = (
    signaturePriority: SignaturePicturePriorityEnum,
    rank: number,
    callback?: () => void,
  ) => {
    void requestDeleteUserPicture({ dto: { rank, signaturePicture: true } })
      .then(() => {
        toastSuccess(t("success.userPicture.delete"));
        changeDefaultSignatureLocally(signaturePriority, rank);
        void requestGetUserSignaturePictures({});
        callback?.();
      })
      .catch(() => {
        toastError(t("error.toast.userPicture.delete"));
      });
  };

  const handleSetDefault = (
    signaturePriority: SignaturePicturePriorityEnum,
    rank?: number,
    callback?: () => void,
  ) => {
    void requestUpdateUserSignaturePriority({
      dto: {
        rank,
        defaultSignature: signaturePriority,
      },
    })
      .then(() => {
        toastSuccess(t("success.userPicture.priority"));
        changeDefaultSignatureLocally(signaturePriority, rank);
        callback?.();
      })
      .catch(() => {
        toastError(t("error.toast.userPicture.priority"));
      });
  };

  const ensureAllRanksPresent = (items: Picture[]) => {
    const maxRank = DEFAULT_PICTURES.length; // Maximum rank as per your requirements
    const ranksPresent = new Set(items.map((item) => item.rank));
    const updatedItems: Picture[] = [];
    const defaultPicture = (rank: number): Picture => ({
      rank,
      picture: "",
      isDefault: false,
    });

    for (let rank = 1; rank <= maxRank; rank++) {
      if (ranksPresent.has(rank)) {
        updatedItems.push(
          items.find((item) => item.rank === rank) ?? defaultPicture(rank),
        );
      } else {
        updatedItems.push(defaultPicture(rank));
      }
    }

    // Sort the array in case new items were added

    updatedItems.sort((a, b) => a.rank - b.rank);

    setSignaturePictures(updatedItems);
  };

  const changeDefaultSignatureLocally = (
    signaturePriority: SignaturePicturePriorityEnum,
    rank?: number,
  ) => {
    signaturePictures.forEach((signaturePicture) => {
      signaturePicture.isDefault = false;
    });

    if (rank) {
      const index = signaturePictures.findIndex(
        (signaturePicture) => signaturePicture.rank === rank,
      );
      const updatedSignaturePictures = [...signaturePictures];
      updatedSignaturePictures[index].isDefault = true;
      setSignaturePictures(updatedSignaturePictures);
    }

    setSignaturePriority(signaturePriority);
  };

  return (
    <CardSection title={t("account.mySignature.signature")}>
      <>
        <p className="m-0">{t("account.mySignature.body")}</p>
        <div className="mt-24">
          <div className="mb-24">
            <h2 className="mb-24">
              {t(
                "account.mySignature.signaturePicture.signatureImagePicture.title",
              )}
              <InfoModal value="loadImage" info />
            </h2>
            <SignaturePicture
              signaturePictures={signaturePictures.filter((picture) =>
                PERSONAL_SIGNATURE_IMAGE_RANKS.includes(picture.rank),
              )}
              onUpdatePicture={handleUpdatePicture}
              onDeletePicture={handleDeletePicture}
              onSetDefaultPicture={handleSetDefault}
            />
          </div>
          <div className="mb-24">
            <h2 className="mb-24">
              {t(
                "account.mySignature.signaturePicture.signatureHandwrittenPicture.title",
              )}
              <InfoModal value="signatureHandwrittenPicture" info />
            </h2>
            <SignatureHandwrittenPicture
              signaturePictures={signaturePictures.filter((picture) =>
                HANDWRITTEN_SIGNATURE_RANKS.includes(picture.rank),
              )}
              onUpdatePicture={handleUpdatePicture}
              onDeletePicture={handleDeletePicture}
              onSetDefaultPicture={handleSetDefault}
            />
          </div>
          <div className="mb-24">
            <h2 className="mb-24">
              {t(
                "account.mySignature.signaturePicture.signatureTextPicture.title",
              )}
              <InfoModal value="signatureTextPicture" info />
            </h2>
            <SignatureTextPicture
              signaturePictures={signaturePictures.filter((picture) =>
                SIGNATURE_TEXT_RANKS.includes(picture.rank),
              )}
              onUpdatePicture={handleUpdatePicture}
              onDeletePicture={handleDeletePicture}
              onSetDefaultPicture={handleSetDefault}
            />
          </div>
          <div className="mb-24">
            <h2 className="mb-24">
              {t(
                "account.mySignature.signaturePicture.noVisibleSignature.title",
              )}
              <InfoModal value="noVisibleSignature" info />
            </h2>
            <SignatureNoPicture
              isDefault={
                signaturePriority === SignaturePicturePriorityEnum.NO_PICTURE
              }
              onSetDefaultPicture={handleSetDefault}
            />
          </div>
          <div className="d-flex align-items-center justify-content-end flex-wrap gap-16 pt-16">
            <div className="flex-grow-1">
              <BasicLink
                to={ROUTES.extranet.menu.offices.preferences.generate()}
              >
                {t("account.mySignature.linkToPreferences")}
              </BasicLink>
            </div>
            {signaturePriority ===
            SignaturePicturePriorityEnum.OFFICE_PICTURE ? (
              <CheckedLabel
                text={t("account.mySignature.makeOfficePictureDefault")}
                isChecked
              />
            ) : (
              <BasicButton
                variant="transparent"
                text={t("account.mySignature.makeOfficePictureDefault")}
                onClick={() => {
                  handleSetDefault(SignaturePicturePriorityEnum.OFFICE_PICTURE);
                }}
              />
            )}
          </div>
        </div>
      </>
    </CardSection>
  );
};

export default MySignaturesSection;
